import React from 'react'

import Cal from '@calcom/embed-react'
import {Box} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import CalendarLoader from '@progyny/components/CalendarLoader'
import type {BookingField} from '@progyny/types/calcom'
import {
  REACT_APP_CALCOM_EMBED_URL,
  REACT_APP_CALCOM_ORIGIN,
} from '@progyny/utils/env'

export interface CalStepProps {
  embedKey: string
  namespace: string
  enhancedCalLink: string
  height?: string | number
  rescheduleUid?: string
  title?: string
  description?: string
  encodedAdditionalContent?: string
  minDate?: string
  maxDate?: string
  selectedLanguage: string
  languageLocked?: boolean
  lockAttendeeFields?: boolean
  prefillData?: Record<string, string | undefined>
  calcomMetadata: Record<string, string>
  calcomRoutingFormResponses: Record<string, string>
  bookingFields?: BookingField[]
  calendarLoaded: boolean
  productLine: string
}

const CalStep: React.FC<CalStepProps> = ({
  embedKey,
  namespace,
  enhancedCalLink,
  height,
  rescheduleUid,
  title,
  description,
  encodedAdditionalContent,
  minDate,
  maxDate,
  selectedLanguage,
  languageLocked,
  lockAttendeeFields,
  prefillData,
  calcomMetadata,
  calcomRoutingFormResponses,
  bookingFields,
  calendarLoaded,
  productLine,
}) => {
  const {classes} = useStyles({height, calendarLoaded})

  return (
    <Box position='relative' minHeight={600}>
      <Box width='100%' className={classes.calWrapper}>
        <Cal
          key={embedKey}
          namespace={namespace}
          calLink={enhancedCalLink}
          style={
            height != null
              ? {width: '100%', height: '100%', overflow: 'auto'}
              : {width: '100%', minHeight: 0}
          }
          config={{
            layout: 'month_view',
            ...(rescheduleUid && {rescheduleUid}),
            ...(title && {titleOverride: title}),
            ...(description && {descriptionOverride: description}),
            ...(encodedAdditionalContent && {
              additionalContent: encodedAdditionalContent,
            }),
            ...(minDate && {minDate}),
            ...(maxDate && {maxDate}),
            ...(selectedLanguage && {'cal.language': selectedLanguage}),
            ...(languageLocked !== undefined && {
              'cal.languageLocked': String(languageLocked),
            }),
            ...(lockAttendeeFields !== undefined && {
              'cal.lockAttendeeFields': String(lockAttendeeFields),
            }),
            ...prefillData,
            ...calcomMetadata,
            ...calcomRoutingFormResponses,
            ...(bookingFields && {
              'cal.bookingFields': JSON.stringify(bookingFields),
            }),
          }}
          calOrigin={REACT_APP_CALCOM_ORIGIN}
          embedJsUrl={REACT_APP_CALCOM_EMBED_URL}
        />
      </Box>
      <Box className={classes.loaderOverlay}>
        <CalendarLoader open={!calendarLoaded} productLine={productLine} />
      </Box>
    </Box>
  )
}

const useStyles = makeStyles<{
  height?: string | number
  calendarLoaded: boolean
}>()((theme, {height, calendarLoaded}) => ({
  calWrapper: {
    ...(height != null
      ? {maxHeight: height, overflow: 'auto'}
      : {overflow: 'visible'}),
    opacity: calendarLoaded ? 1 : 0,
    transition: 'opacity 500ms ease-in-out',
  },
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: calendarLoaded ? 0 : 1,
    transition: 'opacity 500ms ease-in-out',
    pointerEvents: calendarLoaded ? 'none' : 'auto',
  },
}))

export default CalStep
